import React from 'react';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';

const TYPE = { pl: 'Privilege Leave', wfh: 'Work From Home' };
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', weekday: 'short' });
const span = (l) => `${TYPE[l.type] || l.type} · ${fmt(l.start)}${l.end !== l.start ? ` – ${fmt(l.end)}` : ''}${l.half_day ? ' (half)' : ''}`;

function CaseRow({ c, onCharge, onUndo, busy }) {
  return (
    <div className="p-3 rounded-xl border border-neutral-200 bg-white flex flex-col md:flex-row md:items-center gap-3" data-testid={`joined-case-${c.key}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1a1a2e]">{c.employee_name}</p>
        <p className="text-xs text-neutral-600 mt-0.5">{span(c.first)} <span className="text-neutral-400">→</span> {span(c.second)}</p>
        <p className="text-[11px] text-orange-700 mt-0.5">Gap: {c.dates.map(fmt).join(', ')} · {c.days} day(s) to {TYPE[c.charge_type] || c.charge_type}</p>
      </div>
      {c.charged ? (
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-emerald-700" data-testid={`joined-charged-${c.key}`}>Charged {c.charged_by === 'auto' ? '(automatically)' : ''}</span>
          <button type="button" disabled={busy} onClick={() => onUndo(c)} className="text-xs text-neutral-500 hover:underline" data-testid={`joined-undo-${c.key}`}>Undo</button>
        </div>
      ) : (
        <button type="button" disabled={busy} onClick={() => onCharge(c)} data-testid={`joined-charge-${c.key}`}
          className="h-8 px-4 rounded-full bg-[#1a1a2e] text-white text-xs font-semibold hover:bg-black disabled:opacity-40">Charge {c.days} day(s)</button>
      )}
    </div>
  );
}

export default function JoinedLeaveReport({ api }) {
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [data, setData] = React.useState(null);
  const [filter, setFilter] = React.useState('uncharged');
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => api.get('/api/admin/leaves/joined-report', { params: { year } }).then((r) => setData(r.data)).catch(() => setData({ cases: [] })), [api, year]);
  React.useEffect(() => { setData(null); load(); }, [load]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
    setBusy(false);
  };
  const charge = (c) => window.confirm(`Deduct ${c.days} day(s) from ${c.employee_name}'s ${TYPE[c.charge_type] || c.charge_type} balance?`)
    && act(() => api.post('/api/admin/leaves/joined-report/charge', { first_id: c.first.id, second_id: c.second.id }), 'Charged');
  const undo = (c) => act(() => api.delete('/api/admin/leaves/joined-report/charge', { params: { key: c.key } }), 'Charge removed');
  const cases = (data?.cases || []).filter((c) => filter === 'all' || (filter === 'charged' ? c.charged : !c.charged));

  return (
    <div className="space-y-4" data-testid="joined-leave-report">
      <div className="flex flex-wrap items-center gap-3">
        <Link2 className="w-4 h-4 text-orange-600" />
        <p className="flex-1 text-sm text-neutral-600">Leaves (Privilege Leave / Work From Home) separated only by a weekend or holiday. The days in between should be charged.</p>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-9 px-3 rounded-lg border border-neutral-200 text-sm" data-testid="joined-year-select">
          {[0, 1, 2].map((i) => new Date().getFullYear() - i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        {[['uncharged', 'Not charged'], ['charged', 'Charged'], ['all', 'All']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`joined-filter-${k}`}
            className={`px-3 h-8 rounded-full text-xs font-medium border ${filter === k ? 'bg-[#1a1a2e] text-white border-[#1a1a2e]' : 'border-neutral-200 text-neutral-600'}`}>{l}</button>
        ))}
        {data && <span className="text-xs text-neutral-500 ml-auto" data-testid="joined-summary">{data.uncharged} not charged · {data.uncharged_days} day(s) missed</span>}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-neutral-400" /> : (
        <div className="space-y-2">
          {cases.map((c) => <CaseRow key={c.key} c={c} onCharge={charge} onUndo={undo} busy={busy} />)}
          {!cases.length && <p className="text-sm text-neutral-400 py-6 text-center" data-testid="joined-empty">No cases here.</p>}
        </div>
      )}
    </div>
  );
}
